import React from "react";
import Image from "next/image";
import Container from "@/components/commons/Container";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { TactsuitSleeveInteractionScrollContainer } from "@/v2-components/pages/tactsuit/tacsuit-sleeve/container/tactsuit-sleeve-interaction-scroll-container";

/**
 * TactSleeve 인터랙션 + 특징 섹션.
 *
 * 소개 페이지(`src/pages/tactsuit/tactsleeve`)에 로컬 정의돼 있어 구매 상세페이지(PDP)에서
 * 재사용할 수 없었다(#5068). 소개 페이지를 PDP 로 통합하면서 여기로 끌어올린다.
 *
 * ⚠️ 번역 키는 `t("리터럴")` 로 남아 있어야 grep 계약이 성립한다(docs/i18n.md).
 */
interface FeatureSectionProps {
  videoSrc?: string;
  imageSrc?: string;
  title: string;
  description: string;
  reverse?: boolean;
}

function FeatureSection({
  videoSrc,
  imageSrc,
  title,
  description,
  reverse = false,
}: FeatureSectionProps) {
  const mediaElement = videoSrc ? (
    <video
      src={videoSrc}
      className="absolute h-full w-full object-fill"
      autoPlay
      loop
      muted
      playsInline
    />
  ) : (
    <Image src={imageSrc!} alt={title} className="object-contain" fill />
  );

  return (
    <Container className="mt-16 grid max-w-screen-mdl gap-6 laptop:mt-24 laptop:grid-cols-2">
      <div
        className={`relative aspect-square overflow-hidden rounded-2xl ${
          reverse ? "laptop:order-1" : ""
        }`}
      >
        {mediaElement}
      </div>
      <div
        className={cn(
          "flex items-center",
          reverse ? "justify-start" : "justify-center",
        )}
      >
        <div className="flex w-full flex-col gap-3 laptop:max-w-[368px] laptop:gap-5">
          <h2 className="text-3xl laptop:text-5xl">{title}</h2>
          <p className="text-base laptop:whitespace-pre-line laptop:text-lg">
            {description}
          </p>
        </div>
      </div>
    </Container>
  );
}

export function SleeveProductFeatures() {
  const { t } = useTrans(["tactsuit"]);

  return (
    <>
      <div className="mt-16 w-full bg-coolgray-10 laptop:mt-24">
        <TactsuitSleeveInteractionScrollContainer />
      </div>

      <FeatureSection
        videoSrc="/media/tactsuit/sleeve/sleeve-1.mp4"
        title={t("No Slipping. No Shifting")}
        description={t(
          "Anti-slip material keeps the TactSleeve securely in place, even during your more active gameplay moments.",
        )}
      />

      <FeatureSection
        videoSrc="/media/tactsuit/sleeve/sleeve-2.mp4"
        title={t("Move Freely. Stay Locked In")}
        description={t(
          "Whether you're dancing, throwing punches, or training, each TactSleeve stays securely in place.",
        )}
        reverse
      />

      <FeatureSection
        imageSrc="/images/tactsuit/tactsleeve/recoil.png"
        title={t("Powerful Recoil Experience")}
        description={t(
          "Feel intense haptic feedback that delivers authentic recoil sensations in FPS games.",
        )}
      />
    </>
  );
}
